import { MuiFileInput } from 'mui-file-input';
import { FormGroup, Stack, Typography, Box, CircularProgress } from '@mui/material';
import { FieldProps } from 'formik';
import React, { useEffect, useState, useRef } from 'react';
import b64 from 'base64-js';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import { convertPdfToJpg } from '../upload';

interface Props {
    label: string;
    /** Called when the user selects a new file (not when clearing). */
    onFileSelected?: () => void;
}

export default function ImagePicker(props: FieldProps<File | null> & Props) {
    const { form, field } = props;
    const [imageView, setImageView] = useState<React.JSX.Element>(<></>);
    const [isConverting, setIsConverting] = useState(false);
    const meta = form.getFieldMeta(field.name);

    // Use ref for async operations to avoid stale closures
    const currentFileRef = useRef<File | null>(null);

    useEffect(() => {
        let isMounted = true;
        const file = field.value;

        const updatePreview = async (file: File) => {
            try {
                const buf = await file.arrayBuffer();
                if (!isMounted || file !== currentFileRef.current) return;

                const byteArray = new Uint8Array(buf);
                const encoded = b64.fromByteArray(byteArray);
                const dataUrl = `data:${file.type};base64,${encoded}`;
                setImageView(
                    <Box
                        sx={{
                            marginTop: '10px',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                        }}
                    >
                        <img
                            style={{
                                maxWidth: '100%',
                                maxHeight: '500px',
                                height: 'auto',
                                width: 'auto',
                                display: 'block',
                            }}
                            alt="uploaded"
                            src={dataUrl}
                        />
                    </Box>
                );
            } catch (error) {
                console.error('Error processing file:', error);
            }
        };

        currentFileRef.current = file;

        if (file) {
            updatePreview(file);
        } else {
            setImageView(<></>);
        }

        return () => {
            isMounted = false;
        };
    }, [field.value]);

    const handleFileChange = async (newFile: File | null) => {
        if (newFile && newFile.type === 'application/pdf') {
            setIsConverting(true);
            try {
                // Show PDF icon during conversion
                setImageView(
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            border: '1px solid #ccc',
                            borderRadius: '4px',
                            padding: '16px',
                            backgroundColor: '#f5f5f5',
                        }}
                    >
                        <PictureAsPdfIcon sx={{ fontSize: 60, color: '#f44336' }} />
                        <Typography variant="body2" sx={{ mt: 1 }}>
                            {newFile.name} ({(newFile.size / 1024).toFixed(1)} KB)
                        </Typography>
                        <CircularProgress size={24} sx={{ mt: 1 }} />
                    </Box>
                );

                // Convert PDF to JPG immediately when selected
                const convertedFile = await convertPdfToJpg(newFile);
                form.setFieldValue(field.name, convertedFile);
            } catch (error) {
                console.error('Error converting PDF:', error);
                // If conversion fails, set the original PDF file
                form.setFieldValue(field.name, newFile);
            } finally {
                setIsConverting(false);
            }
        } else {
            form.setFieldValue(field.name, newFile);
        }
        if (newFile) props.onFileSelected?.();
        form.setFieldTouched(field.name, true);
    };

    return (
        <FormGroup>
            <Stack spacing={2}>
                <MuiFileInput
                    value={field.value}
                    onChange={handleFileChange}
                    placeholder="Choose file"
                    hideSizeText
                    inputProps={{ accept: 'image/jpeg,image/png,application/pdf' }}
                    error={meta.touched && Boolean(meta.error)}
                    helperText={meta.touched && meta.error ? meta.error : ''}
                    label={props.label}
                    disabled={isConverting}
                />
                {isConverting ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: 2 }}>
                        <Typography variant="body2">Converting PDF to image...</Typography>
                    </Box>
                ) : (
                    imageView
                )}
            </Stack>
        </FormGroup>
    );
}
